import axios from 'axios';
import { TamanhoSummary, HistoryEntry, TamanhoChange, LojistasResponse } from './types';

const client = axios;

type FetchResponse = {
  success: boolean;
  data: {
    tamanho_ul: string;
    total_cnpjs: string;
  }[];
};

export async function fetchTamanhos(): Promise<TamanhoSummary[]> {
  const { data } = await client.get<FetchResponse>('/tamanho-ul');
  const rows = data?.data ?? [];
  return rows.map((row) => ({
    tamanhoUL: String(row.tamanho_ul ?? ''),
    totalCnpjs: parseInt(String(row.total_cnpjs ?? '0'), 10) || 0,
  }));
}

type BatchResponse = {
  success: boolean;
  count?: number;
  changedCount?: number;
  error?: string;
};

export async function batchUpdateTamanhos(
  items: { cnpjlojista: string; tamanho_ul: string }[]
): Promise<BatchResponse> {
  const { data } = await client.post<BatchResponse>(
    '/tamanho-ul/list',
    { items }
  );
  return data;
}

type HistoryRow = {
  tamanho_ul_lojista_history_id: string;
  tamanho_ul_lojista_id: string;
  user_id: string;
  name: string | null;
  email: string | null;
  CNPJLojista: string | null;
  RazaoSocial: string | null;
  NomeFantasia: string | null;
  tamanho_ul: string | null;
  data: unknown;
  created_at: string;
};

type HistoryApiResponse = {
  success: boolean;
  data: HistoryRow[];
};

type ParsedData = {
  type?: string;
  action?: string;
  total_changed?: number;
  changes?: {
    cnpjlojista?: string;
    razao_social?: string;
    razaoSocial?: string;
    nome_fantasia?: string;
    nomeFantasia?: string;
    before?: { tamanho_ul?: string } | null;
    after?: { tamanho_ul?: string } | null;
  }[];
  before?: { tamanho_ul?: string } | null;
  after?: { tamanho_ul?: string } | null;
  cnpjlojista?: string;
};

function extractChanges(parsed: ParsedData, row: HistoryRow): TamanhoChange[] {
  if (Array.isArray(parsed.changes)) {
    return parsed.changes.map((c) => ({
      cnpjLojista: String(c.cnpjlojista ?? row.CNPJLojista ?? ''),
      razaoSocial: String(c.razao_social ?? c.razaoSocial ?? row.RazaoSocial ?? ''),
      nomeFantasia: String(c.nome_fantasia ?? c.nomeFantasia ?? row.NomeFantasia ?? ''),
      valorAnterior: c.before?.tamanho_ul ?? '',
      valorNovo: c.after?.tamanho_ul ?? '',
    }));
  }

  return [{
    cnpjLojista: String(parsed.cnpjlojista ?? row.CNPJLojista ?? ''),
    razaoSocial: String(row.RazaoSocial ?? ''),
    nomeFantasia: String(row.NomeFantasia ?? ''),
    valorAnterior: parsed.before?.tamanho_ul ?? '',
    valorNovo: parsed.after?.tamanho_ul ?? '',
  }];
}

export async function fetchLojistas(
  page: number,
  search: string,
  tamanho?: string
): Promise<LojistasResponse> {
  const params: Record<string, string | number> = { page };
  if (search.trim()) {
    params.search = search.trim();
  }
  if (tamanho) {
    params.tamanho = tamanho;
  }
  const { data } = await client.get<LojistasResponse>('/tamanho-ul/lojistas', { params });
  return data;
}

export async function fetchTamanhoHistory(): Promise<HistoryEntry[]> {
  const historyRes = await client.get<HistoryApiResponse>('/tamanho-ul/history');
  const rows: HistoryRow[] = historyRes.data?.data ?? [];

  const groupedMap = new Map<string, {
    date: string;
    userName: string;
    changes: TamanhoChange[];
  }>();

  for (const row of rows) {
    const userName = row.name || row.email || 'Desconhecido';
    const rawData = row.data ?? '{}';
    const createdAt = row.created_at || '';

    let parsed: ParsedData = {};
    try {
      if (typeof rawData === 'string') {
        parsed = JSON.parse(rawData);
      } else if (rawData && typeof rawData === 'object') {
        parsed = rawData as ParsedData;
      }
    } catch {
      continue;
    }

    const changes = extractChanges(parsed, row).filter((change) =>
      change.cnpjLojista || change.valorAnterior || change.valorNovo
    );
    if (changes.length === 0) continue;

    const groupKey = `${createdAt}__${userName}`;

    const existing = groupedMap.get(groupKey);
    if (existing) {
      existing.changes.push(...changes);
    } else {
      groupedMap.set(groupKey, { date: createdAt, userName, changes });
    }
  }

  const entries: HistoryEntry[] = [];
  let idx = 0;
  for (const [, value] of groupedMap) {
    entries.push({
      id: `history-${idx++}`,
      date: value.date,
      user: value.userName,
      changes: value.changes,
    });
  }

  return entries;
} 
